import AriaMenu from "../util/AriaMenu";
import pubSubGlobal from "../util/pubSubGlobal";
import { shouldBeVisibleByStateCheck } from "../util/Misc";
import { overflowmenuRender } from "./templates/renders/overflowmenu";

/**
 * @typedef  {Object}	OverFlowMenu
 * @property {CIApp}	$app
 * @property {Function}	created
 * @property {Function}	onClick
 * @property {Function}	removed
 * @property {String}	tagName
 * @property {String}	template
 */

/**
 * @type {OverFlowMenu}
 */
const OverFlowMenu: any = {
    created() {
        this.overflowVisible = false;
        this.visible = true;
        const stateMgr = this.$app.getStateMgr();
        this.overFlowMenu = this.el.querySelector(".overflow-menu-items");
        this.overflowButton = this.el.querySelector("#mainMenuOptions");
        this.hideOverflowWhenClickingOutsideMenu();
        this.ariaMenu = new AriaMenu(this.getAriaMenuOptions());

        if (this.prop.hiddenstates && this.visible) {
            this.subscribeToStateMgr();
            this.handleComponentVisibility(stateMgr.state.state);
        }
    },

    subscribeToStateMgr() {
        if (typeof this.stateListenerId === "undefined") {
            this.stateListenerId = this.$app.getStateMgr().subscribe((state) => this.chatStateListener(state), "state");
        }
    },

    focusFirstMenuItem() {
        this.ariaMenu.focusElementByIndex(0);
    },

    /**
     * @returns {AriaMenuOptions}  aria menu options for keyboard navigation
     */
    getAriaMenuOptions() {
        return {
            callbacks: {
                onEscape: this.hideOverflow.bind(this),
                onMenuButtonPress: this.onClick.bind(this),
                onMenuFocusOut: this.hideOverflow.bind(this),
            },
            elements: {
                menuButton: this.el.querySelector('[role="button"]'),
                menuContainer: this.overFlowMenu,
                /**
                 * Need to ignore the overflow items that are hidden by selecting all the items that
                 * do not have attribute `style="display: none"`.
                 *
                 * Overflow items can be hidden by configuring the `hiddenstates` or `hiddendevices`
                 * properties in uiconfig to make some menu options hidden on phones or tablets.
                 */
                menuItems: this.overFlowMenu.querySelectorAll(`overflow-item:not([style*="display: none"]) div`),
            },
        };
    },

    hideOverflow() {
        this.overFlowMenu.classList.remove("enter");
        this.overflowVisible = false;
        this.overflowButton.setAttribute("aria-expanded", false);
        // Added to avoid focus going in overflow menu when not open
        this.overFlowMenu.setAttribute("aria-hidden", true);

        // On closing popup, focus is set to menu button unless modal is open and none of the element is focused
        if (!this.$app.getStateMgr().state.isModalVisible && !document.activeElement) {
            const menuButton = this.overFlowMenu.parentNode.querySelector("#mainMenuOptions");
            menuButton.focus();
        }
    },

    /**
     * @listens Layout#onLayoutClick
     */
    hideOverflowWhenClickingOutsideMenu() {
        this.onLayoutClickSubscription = pubSubGlobal.subscribe(() => {
            if (this.overflowVisible) {
                this.hideOverflow();
            }
        }, "Layout.onClick");
    },

    /**
     * @param {MouseEvent|KeyboardEvent} event	On click event
     */
    onClick(event) {
        if (this.overflowVisible) {
            this.hideOverflow();
        } else {
            this.showOverflow();

            // Delay the focus to let element render
            setTimeout(() => {
                this.focusFirstMenuItem();
            }, 500);
            pubSubGlobal.publish("overflow.clicked", event);
        }

        event.preventDefault();
        event.stopPropagation();
    },

    /**
     * passing engaged state so that if any of the menu buttons need to be enabled
     */
    chatStateListener(state) {
        this.handleComponentVisibility(state)
    },

    handleComponentVisibility(state) {
        if (!this.prop.hiddenstates) {
            return;
        }

        let visibility = shouldBeVisibleByStateCheck(state, this.prop.hiddenstates);

        if (visibility && !this.visible) {
            this.el.style.display = null;
        } else if (!visibility && this.visible) {
            this.el.style.display = "none";
        }

        this.visible = visibility;
    },

    removed() {
        const stateMgr = this.$app.getStateMgr();

        if (typeof this.stateListenerId !== "undefined") {
            stateMgr.unsubscribe(this.stateListenerId);
        }

        this.ariaMenu.destroy();
        this.unregisterEventHandlers();
    },

    showOverflow() {
        this.overflowButton.setAttribute("aria-expanded", true);
        this.overFlowMenu.setAttribute("aria-hidden", false);
        this.overFlowMenu.classList.add("enter");
        this.overflowVisible = true;
    },

    tagName: "overflow-menu",
    renderer: overflowmenuRender,

    unregisterEventHandlers() {
        pubSubGlobal.unsubscribe(this.onLayoutClickSubscription);
    },
};

/**
 * @param 	{CIApp}			appInstance		App instance
 * @return	{OverFlowMenu}					OverFlow Menu
 */
export default appInstance => {
    OverFlowMenu.$app = appInstance;
    return OverFlowMenu;
};
